import { useState } from "react"
import { useDispatch } from "react-redux"
import Button from "react-bootstrap/Button"
import { Container, Title, Text } from "components/Form"
import { members, message, auth } from "state_management"
import useT from "utils/hooks/useT"

// SPEC-057 L2 — self-service email change, shown only on the member's OWN profile. The email is
// the identity anchor (reset codes land there), so moving it is a two-step verification: the
// member re-authenticates with their password and names the new address (step 1 — the backend
// mails a 6-digit code to the NEW mailbox), then confirms with that code (step 2 — the backend
// flips the address and stamps last_logout, so the session may end right after; the auth
// reducer routes any resulting 401 to the login screen). A typo in the new address never locks
// the account: the old address keeps working until the confirmation succeeds.
//
// #4125: this component is now the EXPANDED panel only. The link that opens it sits on the email
// field's own row, so its open/closed state lives in MemberConfig — the row and the panel are
// siblings and cannot both be owned from here.
export default function EmailChangeSection({ onClose }) {
  const dispatch = useDispatch()
  const { t } = useT("common")
  const [newEmail, setNewEmail] = useState("")
  const [password, setPassword] = useState("")
  const [codeSent, setCodeSent] = useState(false)
  const [code, setCode] = useState("")

  const handleSendCode = () => {
    if (!newEmail || !password) {
      dispatch(message.warning(t("emailChangeMissingFields")))
      return
    }
    dispatch(members.requestEmailChange(password, newEmail))
      .then(() => setCodeSent(true))
      // Wrong password / rejected domain — the message layer toasts it; stay in step 1.
      .catch(() => {})
  }

  const handleConfirm = () => {
    if (!code) {
      return
    }
    dispatch(members.confirmEmailChange(code))
      .then(() => {
        // UI-review: the confirmed change revokes every session server-side — end this one
        // deliberately with a proper explanation instead of letting the next request 401 with
        // the misleading "logged out by another device".
        onClose()
        dispatch(message.info(t("emailChangedLoggedOut")))
        dispatch(auth.endSession())
      })
      // Wrong/expired code — toast via the message layer; stay in step 2.
      .catch(() => {})
  }

  return (
    <div className="MemberConfig-email-change">
      <Container>
        <Title>{t("newEmail")}</Title>
        <Text type="email" value={newEmail} disabled={codeSent} onChange={setNewEmail} />
      </Container>
      {!codeSent && (
        <>
          <Container>
            <Title>{t("currentPassword")}</Title>
            <Text type="password" value={password} onChange={setPassword} />
          </Container>
          <Button variant="outline-primary" type="button" onClick={handleSendCode}>
            {t("sendEmailChangeCode")}
          </Button>
        </>
      )}
      {codeSent && (
        <>
          <p className="MemberConfig-credential-hint">{t("emailChangeCodeSentInfo")}</p>
          <Container>
            <Text type="text" placeholder={t("emailChangeCode")} value={code} onChange={setCode} />
          </Container>
          <Button variant="outline-primary" type="button" onClick={handleConfirm}>
            {t("confirmEmailChange")}
          </Button>
        </>
      )}
      <Button variant="link" type="button" onClick={onClose}>
        {t("cancel")}
      </Button>
    </div>
  )
}
